/* eslint-disable max-len */
import React, {type RefObject} from 'react';

/**
 * The `IconMailProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface IconMailProps {
    /**
     * A string representing the CSS class to be applied to the IconMailIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A number or string representing the height of the IconMailIcon element.
     *
     * @default 32
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'IconMail'
     */
    testId?: string;
    /**
     * A number or string representing the height of the IconMailIcon element.
     *
     * @default 32
     */
    width?: number | string;
}

/**
 * The `IconMail` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `IconMailProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the IconMailIcon element to override its default styling.
 * @param props.height    A number or string representing the height of the IconMailIcon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the IconMailIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const IconMailComponent = <IconMail className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const IconMail = ({className = '', height = 32, ref, testId = 'IconMail', width = 32}: IconMailProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 32 32"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path d="M30 8H2a2 2 0 0 0-2 2v3.36l16 6.4 16-6.4V10a2 2 0 0 0-2-2M0 16.62V30c0 1.1.9 2 2 2h28a2 2 0 0 0 2-2V16.62l-16 6.4z" fill="currentColor" transform="translate(0 -3)" />
    </svg>
);

IconMail.displayName = 'IconMail';